import React, { useEffect, useMemo, useState } from "react";
import { api, formatValue } from "@/lib/api";
import PlayerHeadshot, { PosRank, TeamPill } from "@/components/PlayerHeadshot";
import PlayerBadge from "@/components/PlayerBadge";
import DmeTag from "@/components/DmeTag";
import { useFormat } from "@/contexts/FormatContext";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Loader2, Search, X, Plus, ArrowUp, ArrowDown, Sparkles } from "lucide-react";

const FORMATS = [["1qb", "1QB"], ["superflex", "SF"], ["tep", "TEP"]];
const MAX = 4;

export default function Compare() {
  // Drive the page off the GLOBAL value-format toggle (and a synced local toggle) so changing
  // either one actually re-prices the compared players. "auto" resolves to the detected format.
  const { format, setFormat, resolvedFormat } = useFormat();
  const fmt = format === "auto" ? (resolvedFormat || "superflex") : format;
  const [board, setBoard] = useState(null);
  const [picks, setPicks] = useState([]); // player_ids
  const [q, setQ] = useState("");
  const [ai, setAi] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    setBoard(null);
    api.get("/dme-values", { params: { format: fmt, position: "ALL", limit: 600 } })
      .then(r => setBoard(r.data.players || []))
      .catch(() => setBoard([]));
  }, [fmt]);

  const byId = useMemo(() => Object.fromEntries((board || []).map(p => [String(p.player_id), p])), [board]);
  const selected = picks.map(id => byId[id]).filter(Boolean);

  const results = useMemo(() => {
    if (!q.trim() || !board) return [];
    const ql = q.toLowerCase();
    return board.filter(p => p.name.toLowerCase().includes(ql) && !picks.includes(String(p.player_id))).slice(0, 6);
  }, [q, board, picks]);

  const add = (p) => { if (picks.length < MAX) { setPicks([...picks, String(p.player_id)]); setQ(""); setAi(null); } };
  const remove = (id) => { setPicks(picks.filter(x => x !== id)); setAi(null); };

  const runAi = async () => {
    if (selected.length < 2) return;
    setAiLoading(true);
    setAi(null);
    try {
      const r = await api.post("/compare/ai", { player_ids: picks, format: fmt });
      setAi(r.data.analysis);
    } catch {
      setAi("AI comparison is unavailable right now. Please try again shortly.");
    } finally {
      setAiLoading(false);
    }
  };

  const best = (key, hi = true) => {
    if (selected.length < 2) return null;
    const vals = selected.map(p => p[key] ?? 0);
    return hi ? Math.max(...vals) : Math.min(...vals);
  };
  const bestDme = best("dme"), bestAge = best("age", false);

  const Row = ({ label, render, hint }) => (
    <tr className="border-t border-[var(--border)]">
      <td className="py-3 pr-4 text-[10px] tracking-widest text-[var(--text-3)] font-mono align-middle whitespace-nowrap">{label}{hint}</td>
      {selected.map(p => <td key={p.player_id} className="py-3 px-3 text-center align-middle">{render(p)}</td>)}
    </tr>
  );

  return (
    <div className="space-y-6 fade-up" data-testid="compare-page">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[var(--accent)] text-xs tracking-widest mb-1 font-mono">// PLAYER COMPARISON</div>
          <h1 className="font-display text-4xl font-black uppercase tracking-tight" data-testid="compare-heading">Compare Players</h1>
          <p className="text-[var(--text-2)] mt-2 max-w-2xl flex flex-wrap items-center gap-1">Stack up to {MAX} players side-by-side on <span className="inline-flex items-center gap-1 text-[var(--accent)]">DME value <DmeTag /></span>, positional rank, trend, age and market signals.</p>
        </div>
        <div className="flex items-center gap-1 border border-[var(--border)] rounded-sm p-0.5">
          {FORMATS.map(([k, v]) => (
            <button key={k} onClick={() => setFormat(k)} data-testid={`compare-fmt-${k}`}
              className={`text-xs px-3 py-1.5 rounded-sm transition-colors ${fmt === k ? "bg-[var(--accent)] text-black font-semibold" : "text-[var(--text-2)] hover:text-white"}`}>{v}</button>
          ))}
        </div>
      </header>

      {/* Search / add */}
      {picks.length < MAX && (
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-3)]" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search a player to add…" data-testid="compare-search"
            className="input-dark w-full !pl-10" />
          {results.length > 0 && (
            <div className="absolute z-20 mt-1 w-full card-bordered overflow-hidden" data-testid="compare-search-results">
              {results.map(p => (
                <button key={p.player_id} onClick={() => add(p)} data-testid={`compare-add-${p.player_id}`}
                  className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-white/5 text-left">
                  <span>{p.name} <span className="text-[var(--text-3)] text-xs">{p.position} · {p.team}</span></span>
                  <span className="inline-flex items-center gap-2"><span className="font-mono text-[var(--accent)]">{formatValue(p.dme)}</span><Plus className="w-3.5 h-3.5 text-[var(--text-3)]" /></span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {board === null ? (
        <div className="card-bordered p-12 flex justify-center"><Loader2 className="w-6 h-6 spin text-[var(--accent)]" /></div>
      ) : selected.length === 0 ? (
        <div className="card-bordered p-10 text-center text-sm text-[var(--text-3)]" data-testid="compare-empty">Search and add players above to start comparing.</div>
      ) : (
        <div className="card-bordered overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="py-3 pr-4 text-left text-[10px] tracking-widest text-[var(--text-3)] font-mono w-40">PLAYER</th>
                {selected.map(p => (
                  <th key={p.player_id} className="py-3 px-3 text-center min-w-[150px]">
                    <div className="flex flex-col items-center gap-1.5">
                      <button onClick={() => remove(String(p.player_id))} className="self-end text-[var(--text-3)] hover:text-red-400" data-testid={`compare-remove-${p.player_id}`}><X className="w-3.5 h-3.5" /></button>
                      <PlayerHeadshot playerId={p.player_id} name={p.name} team={p.team} size={44} />
                      <span className="font-display font-bold flex items-center gap-1.5">{p.name}<PlayerBadge badge={p.badge} size={14} /></span>
                      <div className="flex items-center gap-1.5">
                        {p.pos_rank && <PosRank rank={p.pos_rank} delta={p.pos_rank_delta} />}
                        {p.team && <TeamPill team={p.team} />}
                      </div>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <Row label="DME VALUE" hint={<DmeTag className="ml-1" />} render={p => (
                <span className={`font-mono text-lg ${p.dme === bestDme ? "text-[var(--accent)] font-bold" : ""}`} data-testid={`compare-dme-${p.player_id}`}>{formatValue(p.dme)}</span>
              )} />
              <Row label="OVERALL RANK" render={p => <span className="font-mono">#{p.rank}</span>} />
              <Row label="POSITION RANK" render={p => <PosRank rank={p.pos_rank} delta={p.pos_rank_delta} />} />
              <Row label="7D TREND" render={p => (
                p.pos_rank_delta ? (
                  <span className={`font-mono text-xs inline-flex items-center gap-0.5 ${p.pos_rank_delta > 0 ? "text-[var(--accent)]" : "text-[var(--negative)]"}`}>
                    {p.pos_rank_delta > 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}{Math.abs(p.pos_rank_delta)}
                  </span>
                ) : <span className="text-[var(--text-3)]">—</span>
              )} />
              <Row label="AGE" render={p => <span className={`font-mono ${p.age && p.age === bestAge ? "text-[var(--accent)] font-bold" : ""}`}>{p.age ? `${p.age}y` : "—"}</span>} />
              <Row label="SENTIMENT" render={p => <span className="font-mono text-xs" style={{ color: "#EF4444" }}>{p.sentiment ? formatValue(p.sentiment) : "—"}</span>} />
              <Row label="REAL TRADES" render={p => <span className="font-mono text-xs" style={{ color: "#9CA3AF" }}>{p.trades ? formatValue(p.trades) : "—"}</span>} />
            </tbody>
          </table>
        </div>
      )}

      {selected.length >= 2 && (
        <div data-testid="compare-ai-section">
          {!ai && !aiLoading && (
            <button onClick={runAi} data-testid="compare-ai-btn"
              className="btn-primary inline-flex items-center gap-2 text-sm">
              <Sparkles className="w-4 h-4" /> AI Breakdown
            </button>
          )}
          {aiLoading && (
            <div className="card-bordered p-6 flex items-center gap-3 text-sm text-[var(--text-3)]" data-testid="compare-ai-loading">
              <Loader2 className="w-5 h-5 spin text-[var(--accent)]" /> Analyzing the difference between these players…
            </div>
          )}
          {ai && (
            <div className="card-bordered p-5" data-testid="compare-ai-result">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-xs tracking-widest text-[var(--accent)] font-mono">
                  <Sparkles className="w-3.5 h-3.5" /> AI HEAD-TO-HEAD
                </div>
                <button onClick={runAi} className="text-[10px] font-mono uppercase tracking-wider text-[var(--text-3)] hover:text-[var(--accent)]" data-testid="compare-ai-refresh">Regenerate</button>
              </div>
              <div className="prose-dme text-sm text-[var(--text-2)] max-w-none">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{ai}</ReactMarkdown>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
